// One person's surveys (customer, bride, groom, matchmaker, vendor): every invitation sent to them,
// how it went out, which link they opened and answered through, and their answers (not shown for
// anonymous surveys, where only the fact that they answered is recorded).
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Stack, Typography, Chip, Paper, Box, LinearProgress, Alert, Collapse, Button, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { SURVEY_PERSON_HISTORY_QUERY } from '../graphql/queries';

export const VIA_LABEL = { email: 'Email link', whatsapp: 'WhatsApp link', in_app: 'In-app', link: 'Copied link', sms: 'SMS link' };
const STATUS_COLOR = { completed: 'success', sent: 'info', opened: 'info', held: 'warning', pending: 'default', expired: 'default', failed: 'error' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null);
const show = (a) => (a.value == null || a.value === '' ? '—' : Array.isArray(a.value) ? a.value.join(', ') : a.type === 'rating' ? `${a.value} ★` : a.type === 'yesno' ? (a.value === 'yes' ? 'Yes' : 'No') : String(a.value));

function Invitation({ i }) {
  const [open, setOpen] = useState(false);
  const sent = [...new Map((i.deliveries || []).map(d => [d.channel, d])).values()];
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700}>{i.surveyTitle}</Typography>
          <Typography variant="body2" color="text.secondary">{i.subjectLabel ? `About ${i.subjectLabel}` : 'General'}{i.anonymous ? ' · anonymous survey' : ''}</Typography>
        </Box>
        <Chip size="small" label={i.status} color={STATUS_COLOR[i.status] || 'default'} />
        <Tooltip title="Copy this person’s link"><IconButton size="small" onClick={() => { try { navigator.clipboard.writeText(i.link); } catch (e) { /* ignore */ } }}><ContentCopyIcon fontSize="inherit" /></IconButton></Tooltip>
      </Stack>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mt={1}>
        {sent.map(d => <Chip key={d.channel} size="small" variant="outlined" label={`Sent by ${d.channel.replace('_', '-')}: ${d.status}`} color={d.status === 'sent' ? 'success' : d.status === 'failed' ? 'error' : 'default'} />)}
        {i.remindersSent > 0 && <Chip size="small" variant="outlined" label={`${i.remindersSent} reminder(s)`} />}
        {i.openedVia && <Chip size="small" variant="outlined" color="info" label={`Opened: ${VIA_LABEL[i.openedVia] || i.openedVia}`} />}
        {i.completedVia && <Chip size="small" color="success" label={`Answered: ${VIA_LABEL[i.completedVia] || i.completedVia}`} />}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
        {[i.sentAt && `Sent ${fmt(i.sentAt)}`, i.openedAt && `opened ${fmt(i.openedAt)}`, i.completedAt && `answered ${fmt(i.completedAt)}`, !i.completedAt && i.expiresAt && `closes ${fmt(i.expiresAt)}`].filter(Boolean).join(' · ')}
      </Typography>
      {i.completedAt && i.anonymous && <Typography variant="caption" color="text.secondary" display="block">Anonymous survey: their answers are in the results but not linked to them.</Typography>}
      {i.response && (
        <>
          <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => setOpen(o => !o)}>{open ? 'Hide answers' : 'Show answers'}</Button>
          <Collapse in={open}>
            <Stack spacing={1} mt={0.5}>
              {i.response.answers.map((a, k) => (
                <Box key={k}>
                  <Typography variant="body2" fontWeight={600}>{a.label}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }} color={a.value == null ? 'text.secondary' : 'text.primary'}>{show(a)}</Typography>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </>
      )}
    </Paper>
  );
}

export default function PersonSurveys({ userId, onClose }) {
  const { data, loading, error } = useQuery(SURVEY_PERSON_HISTORY_QUERY, { variables: { userId }, fetchPolicy: 'network-only' });
  const h = data?.surveyPersonHistory;
  const answered = (h?.invitations || []).filter(i => i.status === 'completed').length;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>{h?.person?.name || 'Surveys'}</Typography>
          {h && <Typography variant="body2" color="text.secondary">{h.person.email} · {h.person.roleLabel} · {h.invitations.length} invitation(s), {answered} answered</Typography>}
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error.message}</Alert>}
        <Stack spacing={1.5}>
          {(h?.invitations || []).map(i => <Invitation key={i.id} i={i} />)}
          {h && !h.invitations.length && <Typography color="text.secondary">No surveys have been sent to this person yet.</Typography>}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
